// frontend/src/services/core/ApiClient.js
/**
 * Klasa bazowa dla klienta API - obsługuje podstawowe operacje HTTP
 */
export class ApiClient {
  constructor(baseURL, defaultHeaders = {}) {
    this.baseURL = baseURL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...defaultHeaders
    };
  }

  /**
   * Pobierz token autoryzacyjny
   */
  getAuthToken() {
    return localStorage.getItem('access_token');
  }

  /**
   * Przygotuj nagłówki żądania
   */
  prepareHeaders(additionalHeaders = {}) {
    const token = this.getAuthToken();
    return {
      ...this.defaultHeaders,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...additionalHeaders
    };
  }

  /**
   * Wykonaj żądanie HTTP
   */
  async request(url, options = {}) {
    const {
      method = 'GET',
      body = null,
      headers = {},
      ...fetchOptions
    } = options;

    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}/api${url}`;
    
    const requestOptions = {
      method,
      headers: this.prepareHeaders(headers),
      ...(body ? { body: typeof body === 'string' ? body : JSON.stringify(body) } : {}),
      ...fetchOptions
    };

    try {
      const response = await fetch(fullUrl, requestOptions);
      
      // Obsługa błędów HTTP
      if (!response.ok) {
        await this.handleHttpError(response);
      }

      // Zwróć dane jako JSON lub pusty obiekt
      return await response.json().catch(() => ({}));
    } catch (error) {
      // Nie loguj tutaj - zostaw to dla wyższych warstw
      throw error;
    }
  }

  /**
   * Obsługa błędów HTTP
   */
  async handleHttpError(response) {
    let errorData = null;
    let errorMessage = `API error: ${response.status}`;
    
    // Dla błędu 401 - nieautoryzowany dostęp
    if (response.status === 401) {
      // Tutaj można dodać logikę odświeżania tokenu
      const error = new Error('Unauthorized access');
      error.status = response.status;
      throw error;
    }
    
    // Spróbuj sparsować komunikat błędu z backend
    try {
      errorData = await response.json();
      
      // Obsłuż różne formaty błędów z backend
      if (errorData.status === "error") {
        // Format z NestHub: {status: "error", message: "...", error_code: "..."}
        errorMessage = errorData.message || 'Unknown error';
      } else if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.detail) {
        errorMessage = errorData.detail;
      }
    } catch (e) {
      // Jeśli parsing się nie udał, zostaw domyślny komunikat
      console.warn('Failed to parse error response:', e);
    }
    
    // Stwórz Enhanced Error Object z dodatkowymi informacjami
    const error = new Error(errorMessage);
    error.status = response.status;
    error.response = {
      status: response.status,
      data: errorData || {}
    };
    
    throw error;
  }

  // === CONVENIENCE METHODS ===

  async get(url, options = {}) {
    return this.request(url, { method: 'GET', ...options });
  }

  async post(url, body = null, options = {}) {
    return this.request(url, { method: 'POST', body, ...options });
  }

  async put(url, body = null, options = {}) {
    return this.request(url, { method: 'PUT', body, ...options });
  }

  async patch(url, body = null, options = {}) {
    return this.request(url, { method: 'PATCH', body, ...options });
  }

  async delete(url, options = {}) {
    return this.request(url, { method: 'DELETE', ...options });
  }
}
